import { useState } from 'react';
import { ApiError, api } from '../api/client';
import { Banner, EmptyState, ErrorState, Loading } from '../components/states';
import { useAsync } from '../hooks/useAsync';
import { useT } from '../i18n/I18nProvider';

interface ResolutionOption {
  resolution: 'KEEP_LOCAL' | 'IMPORT' | 'REPLACE';
  consoleManages: boolean;
  preservesCurrentState: boolean;
  changesEdgeNow: boolean;
  summary: string;
}

interface Conflict {
  id: string;
  deviceId: string;
  kind: string;
  blocking: boolean;
  resourceType: string;
  resourceKey: string;
  observedKey: string;
  namespace: string;
  detail: string;
  explanation: string;
  offered: ResolutionOption[];
}

/**
 * Conflicts awaiting a decision.
 *
 * The page leads with the fact that nothing has been changed, because that is
 * the thing an operator most needs to know when a deployment stops: the console
 * did not touch their configuration, it stopped and asked.
 *
 * Each option states what it costs before it is taken. The destructive one is
 * styled as destructive and is never preselected — there is no safe default
 * here, and offering one would make the decision look like a formality.
 */
export function ConflictsPage(): JSX.Element {
  const t = useT();
  const conflicts = useAsync(() => api.get<Conflict[]>('/conflicts'), []);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const onResolve = async (id: string, resolution: string): Promise<void> => {
    setBusy(id);
    setError(null);
    try {
      await api.post(`/conflicts/${id}/resolve`, { resolution });
      conflicts.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.unexpected'));
    } finally {
      setBusy(null);
    }
  };

  if (conflicts.loading) return <Loading />;
  if (conflicts.error)
    return <ErrorState message={conflicts.error.message} onRetry={conflicts.reload} />;

  const items = conflicts.data ?? [];

  return (
    <section>
      <div className="page-head">
        <h1>{t('conflict.title')}</h1>
      </div>

      {error ? <Banner variant="error">{error}</Banner> : null}

      {items.length === 0 ? (
        <EmptyState hint={t('conflict.empty')} />
      ) : (
        <>
          <Banner variant="info">{t('conflict.nothingOverwritten')}</Banner>

          {items.map((conflict) => (
            <div className="card" key={conflict.id}>
              <div className="page-head">
                <h2>{t(`conflict.kind.${conflict.kind}`)}</h2>
                <span className={`chip chip--${conflict.blocking ? 'error' : 'warning'}`}>
                  {conflict.blocking ? t('conflict.blocking') : t('conflict.advisory')}
                </span>
              </div>

              <p>{conflict.explanation}</p>
              <p className="muted">
                {conflict.blocking ? t('conflict.blockingHint') : t('conflict.advisoryHint')}
              </p>

              <dl className="kv">
                <dt>{t('conflict.observedObject')}</dt>
                <dd>
                  <code>{conflict.observedKey}</code>
                </dd>
                <dt>{t('conflict.intendedObject')}</dt>
                <dd>
                  <code>{conflict.resourceKey}</code>
                </dd>
              </dl>

              <div className="row-actions">
                {conflict.offered.map((option) => (
                  <button
                    key={option.resolution}
                    type="button"
                    className={`btn btn--ghost${option.changesEdgeNow ? ' btn--danger' : ''}`}
                    disabled={busy !== null}
                    title={option.summary}
                    onClick={() => void onResolve(conflict.id, option.resolution)}
                  >
                    {t(`conflict.resolution.${option.resolution}`)}
                  </button>
                ))}
              </div>

              <ul className="muted">
                {conflict.offered.map((option) => (
                  <li key={option.resolution}>
                    <strong>{t(`conflict.resolution.${option.resolution}`)}</strong>{' '}
                    {option.summary}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </>
      )}
    </section>
  );
}
